import axios, { type InternalAxiosRequestConfig } from 'axios'
import { useAuthStore } from '@/stores/auth'

// Récupérer la base URL depuis les variables d'environnement
// Si VITE_API_URL n'est pas définie ou est vide, utiliser /api comme préfixe par défaut
const apiUrl = import.meta.env.VITE_API_URL?.trim() || '/api'

// Détecter l'environnement pour le logging (utiliser import.meta.env.DEV si besoin)

// Créer une instance axios avec la configuration de base
const axiosInstance = axios.create({
  baseURL: apiUrl,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Ajouter un intercepteur pour ajouter le token d'authentification
axiosInstance.interceptors.request.use(
  (config) => {
    const authStore = useAuthStore()
    const token = authStore.accessToken
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

export default axiosInstance

// Etat partagé pour coalescer les refresh
let isRefreshing = false
let refreshPromise: Promise<boolean> | null = null
type Subscriber = (token: string) => void
const subscribers: Subscriber[] = []
const addSubscriber = (cb: Subscriber) => subscribers.push(cb)
const onRefreshed = (token: string) => { subscribers.splice(0).forEach(cb => cb(token)) }

// Intercepteur de réponse pour gérer les 401 et rafraîchir le token (coalescé)
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = (error?.config || {}) as InternalAxiosRequestConfig & { _retry?: boolean }
    const status = error?.response?.status
    const url: string = originalRequest?.url || ''

    // Ne pas tenter de refresh sur les endpoints d'auth
    const isAuthEndpoint = url.includes('/auth/token/refresh/') || url.includes('/auth/login/') || url.includes('/auth/logout/')
    if (isAuthEndpoint) return Promise.reject(error)

    if (status === 401) {
      // Eviter le double retry pour cette requête
      if (originalRequest._retry) {
        return Promise.reject(error)
      }
      originalRequest._retry = true

      const authStore = useAuthStore()

      // Si un refresh est déjà en cours, s'abonner et attendre
      if (isRefreshing && refreshPromise) {
        return new Promise((resolve, reject) => {
          addSubscriber((token: string) => {
            try {
              originalRequest.headers = originalRequest.headers || {}
              originalRequest.headers.Authorization = `Bearer ${token}`
              resolve(axiosInstance(originalRequest))
            } catch (e) {
              reject(e)
            }
          })
        })
      }

      // Démarrer un nouveau refresh
      isRefreshing = true
      if (!refreshPromise) {
        refreshPromise = authStore.refreshToken() // Promise<boolean>
      }

      try {
        const refreshed = await refreshPromise
        if (refreshed) {
          const token = authStore.accessToken
          if (token) onRefreshed(token)
          // Rejouer la requête courante
          if (token) {
            originalRequest.headers = originalRequest.headers || {}
            originalRequest.headers.Authorization = `Bearer ${token}`
          }
          return axiosInstance(originalRequest)
        } else {
          // Echec refresh
          authStore.clearAuth()
          return Promise.reject(error)
        }
      } finally {
        isRefreshing = false
        refreshPromise = null
      }
    }

    return Promise.reject(error)
  }
)
